import { useEffect, useState } from 'react';
import { normaliseHexColour } from 'src/secret-menu/brand-asset-builder/lib/hexColour';
import type { BrandAssetBuilderPalette } from 'src/secret-menu/brand-asset-builder/types';

// Mantine's `ColorInput` `onChangeEnd` cannot be trusted to fire only for completed, atomic
// colour selections: it checks typed text against ALL of its supported colour formats (hex, hexa,
// rgb, rgba, hsl, hsla), so a partially-typed plain hex value can transiently match a different
// format's regex mid-keystroke (e.g. 4 characters into a 6-digit hex matching the "hexa"
// 3-digit-plus-alpha shorthand) and fire `onChangeEnd` with a wrong, misinterpreted colour before
// typing has finished - silently corrupting the palette with the wrong value if that were
// committed directly. So this never commits from `onChange`/`onChangeEnd` while a field is being
// typed into: `drafts` holds whatever free text is currently shown in each field, and only
// `handleBlur`'s own hex-specific validation/normalisation (`normaliseHexColour`, not Mantine's
// multi-format one) commits the change - or reverts the field back to the last-committed value if
// what's there doesn't validate as clean hex. This also covers swatch-click/picker-drag/eyedropper
// interactions: there's no way to distinguish those from typing at the public `ColorInput`/plain
// `TextInput` level, so every interaction goes through the same draft-then-commit-on-blur path for
// consistency and simplicity.
export function usePaletteColourDrafts(
  palette: BrandAssetBuilderPalette,
  onCommit: (key: keyof BrandAssetBuilderPalette, value: string) => void,
) {
  const [drafts, setDrafts] = useState<BrandAssetBuilderPalette>(palette);
  const [focusedField, setFocusedField] = useState<keyof BrandAssetBuilderPalette | null>(null);

  useEffect(() => {
    if (focusedField === null) {
      setDrafts(palette);
    }
  }, [palette, focusedField]);

  const draftColour = (key: keyof BrandAssetBuilderPalette, value: string) => {
    setDrafts(prev => ({ ...prev, [key]: value }));
  };

  const handleFocus = (key: keyof BrandAssetBuilderPalette) => {
    setFocusedField(key);
  };

  const handleBlur = (key: keyof BrandAssetBuilderPalette) => {
    setFocusedField(null);
    const normalised = normaliseHexColour(drafts[key]);
    if (normalised === null) {
      setDrafts(prev => ({ ...prev, [key]: palette[key] }));
      return;
    }
    setDrafts(prev => ({ ...prev, [key]: normalised }));
    if (normalised !== palette[key]) {
      onCommit(key, normalised);
    }
  };

  return { drafts, draftColour, handleFocus, handleBlur };
}
